import { Injectable } from '@angular/core';
import * as echarts from 'echarts';
import { SelfServiceDataUtilsService } from './self-service-data-utils.service';
import {
  AggregationType,
  ChartPreviewKind,
  ChartShapeOptions,
  ChartStyle,
  ChartTypeOption,
  CustomAggregation,
  MappingKey,
  SupportedChartType,
  UploadedDataset,
} from './self-service-types';

export interface ChartOptionContext {
  chartTypes: ChartTypeOption[];
  shapeOptions: ChartShapeOptions | null;
  customAggregations: CustomAggregation[];
  builtinAggregations: Array<{ label: string; value: AggregationType }>;
}

@Injectable({ providedIn: 'root' })
export class SelfServiceChartOptionBuilderService {
  private ctx!: ChartOptionContext;
  // The title that will actually render for the current build. A chart's title
  // is `style.titleOverride || titleText` (titleText is usually the chart name),
  // so legend/grid spacing must treat the chart as titled even when there's no
  // explicit override — otherwise a top legend overlaps the title.
  private currentTitleText = '';

  constructor(private readonly dataUtils: SelfServiceDataUtilsService) {}

  buildChartOption(
    chartType: SupportedChartType,
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
    context: ChartOptionContext,
  ): echarts.EChartsOption {
    this.ctx = context;
    this.currentTitleText = titleText || '';
    const previewKind = this.ctx.chartTypes.find((item) => item.value === chartType)?.previewKind || 'axis-bar';

    let option: echarts.EChartsOption;
    switch (previewKind) {
      case 'pie':
        option = this.buildPieOption(false, mapping, aggregation, dataset, style, titleText);
        break;
      case 'donut':
        option = this.buildPieOption(true, mapping, aggregation, dataset, style, titleText);
        break;
      case 'treemap':
        option = this.buildTreemapOption(mapping, aggregation, dataset, style, titleText);
        break;
      case 'scatter':
        option = this.buildScatterOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'bubble':
        option = this.buildBubbleOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'quadrant-bubble':
        option = this.buildQuadrantBubbleOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'horizontal-bar':
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText, { horizontal: true });
        break;
      case 'stacked-bar':
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText, { stacked: true });
        break;
      case 'stacked-horizontal-bar':
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText, { horizontal: true, stacked: true });
        break;
      case 'stacked-line':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { stacked: true });
        break;
      case 'stacked-area':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { area: true, stacked: true });
        break;
      case 'area':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { area: true });
        break;
      case 'axis-line':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText);
        break;
      case 'step-line':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { step: true });
        break;
      case 'bar-line':
        option = this.buildBarLineOption(mapping, aggregation, dataset, style, seriesField, titleText);
        break;
      case 'waterfall':
        option = this.buildWaterfallOption(mapping, aggregation, dataset, style, titleText);
        break;
      case 'waterfall-stack':
        option = this.buildWaterfallStackOption(mapping, aggregation, dataset, style, seriesField, titleText);
        break;
      case 'gantt':
        option = this.buildGanttOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'axis-bar':
      default:
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText);
        break;
    }

    // Axis names were being clipped at the chart edges (e.g. "Units"/"year" at
    // the top, "Region"/"region" truncated to "R"/"re" at the right) because the
    // ECharts default places value-axis names at 'end' (top) and category-axis
    // names at 'end' (right) with no reserved space. Centre them along their
    // axis and reserve grid room so they render fully in both preview and the
    // published dashboard view (both go through this single builder).
    this.normalizeAxisNames(option);
    // Size a left/right (vertical) legend to its widest label and reserve the
    // matching grid room so long legend names don't run over the axis name/plot.
    this.normalizeLegendSpacing(option, style);
    // Finally, clamp the accumulated pixel insets so the plot keeps a healthy
    // share of a short card. Secondary Y axis + X2 axis + top legend + title each
    // reserve fixed pixels; on the ~240px builder/published cards they stack up
    // and crush the plot into a thin band (the reported "compressed" charts).
    return this.clampGridForShortCards(option);
  }

  /** The self-service builder preview and the published dashboard both render
   *  these charts into short, fixed-height cards (~240px). The grid insets are
   *  built from absolute pixels — top (title + legend), bottom (X + X2 axis
   *  names), left/right (axis names + vertical legend). With a secondary Y axis
   *  AND a secondary X axis AND a top legend those reservations sum to more than
   *  the card can spare, leaving a sliver of plot area (labels overlap, bars
   *  collapse). ECharts can't know the pixel height at option-build time, so we
   *  cap the COMBINED insets to a fraction of the reference card size, scaling
   *  every side down proportionally. Tall/large cards resize up at runtime, so
   *  this only ever prevents the crushed-plot case — it never adds dead space. */
  private clampGridForShortCards(option: echarts.EChartsOption): echarts.EChartsOption {
    const opt = option as Record<string, unknown>;
    const grid = opt['grid'] as Record<string, unknown> | undefined;
    if (!grid) return option;

    // Reference card the insets are budgeted against (matches .dashboard-chart).
    const REF_H = 240;
    const REF_W = 360;
    // Keep at least this share of each dimension for the actual plot.
    const MIN_PLOT_RATIO = 0.5;

    const numeric = (v: unknown, fallback: number): number => {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    const scaleAxis = (aKey: string, bKey: string, budget: number): void => {
      const a = numeric(grid[aKey], 0);
      const b = numeric(grid[bKey], 0);
      const total = a + b;
      if (total <= budget || total <= 0) return;
      const factor = budget / total;
      grid[aKey] = Math.round(a * factor);
      grid[bKey] = Math.round(b * factor);
    };

    // Only clamp the sides expressed in pixels; percentage insets already scale.
    const isPx = (v: unknown): boolean => typeof v === 'number' || (typeof v === 'string' && !String(v).includes('%'));
    if (isPx(grid['top']) && isPx(grid['bottom'])) {
      scaleAxis('top', 'bottom', Math.round(REF_H * (1 - MIN_PLOT_RATIO)));
    }
    if (isPx(grid['left']) && isPx(grid['right'])) {
      scaleAxis('left', 'right', Math.round(REF_W * (1 - MIN_PLOT_RATIO)));
    }
    return option;
  }

  /** Width (px) of a legend label string at the ~12px legend font. */
  private legendLabelPx(label: string): number {
    return String(label || '').length * 6.5;
  }

  /** For a vertical (left/right) legend, set its width to fit the widest series
   *  name (capped) and widen grid.left/right to clear that band + the axis name,
   *  so long legend labels don't overlap the y-axis name or the plot. Horizontal
   *  (top/bottom) legends are left untouched. */
  private normalizeLegendSpacing(option: echarts.EChartsOption, style: ChartStyle): echarts.EChartsOption {
    if (!style.showLegend) return option;
    const pos = style.legendPosition;
    if (pos !== 'left' && pos !== 'right') return option;

    const opt = option as Record<string, unknown>;
    const legend = (Array.isArray(opt['legend']) ? opt['legend'][0] : opt['legend']) as Record<string, unknown> | undefined;
    if (!legend) return option;

    const series = (Array.isArray(opt['series']) ? opt['series'] : opt['series'] ? [opt['series']] : []) as Array<Record<string, unknown>>;
    const names = series.map((s) => String(s['name'] ?? '')).filter(Boolean);
    if (!names.length) return option;

    const widest = names.reduce((m, n) => Math.max(m, this.legendLabelPx(n)), 0);
    // Legend = colour marker (~14px) + gap + text. Cap so a runaway label still
    // leaves room for the plot; ECharts wraps/scrolls anything past the cap.
    const legendWidth = Math.min(220, Math.max(96, Math.round(widest + 20)));
    legend['width'] = legendWidth;

    // A value axis on the SAME side carries a centred name + tick labels that
    // also live in this band (e.g. "Total" on the right, "cpu" on the left) —
    // add room for them so the legend doesn't sit on top of the axis name.
    const yAxes = (Array.isArray(opt['yAxis']) ? opt['yAxis'] : opt['yAxis'] ? [opt['yAxis']] : []) as Array<Record<string, unknown>>;
    const sideHasNamedAxis = yAxes.some((a) => {
      if (!a || !a['name']) return false;
      const axisPos = a['position'] || 'left';
      return axisPos === pos;
    });
    const axisRoom = sideHasNamedAxis ? 56 : 0;

    // Reserve: legend (at 8px from edge) + legend width + gap + axis name/labels.
    const reserve = 8 + legendWidth + 16 + axisRoom;
    const grid = (opt['grid'] as Record<string, unknown> | undefined) ?? {};
    if (pos === 'left') {
      grid['left'] = Math.max(Number(grid['left'] || 0), reserve);
    } else {
      grid['right'] = Math.max(Number(grid['right'] || 0), reserve);
    }
    opt['grid'] = grid;
    return option;
  }

  /** Centre every axis `name` along its axis with a sensible gap so names sit
   *  clear of the labels instead of overflowing the grid edges. Only touches
   *  axes that actually have a name; leaves everything else untouched. */
  private normalizeAxisNames(option: echarts.EChartsOption): echarts.EChartsOption {
    let hasRightNamedAxis = false;
    let rightAxisLabelPx = 0;
    // Estimate the widest tick label (in px) for each value axis by running its
    // formatter over the series data. The centred axis name sits nameGap px from
    // the axis line; a fixed 52px gap is smaller than wide labels like
    // "700,000%", so the rotated name ("Year"/"Trend") lands on top of the
    // numbers (the reported overlap). We size the gap from the real labels.
    const valueLabelPxByAxis = this.estimateValueLabelPxByAxis(option);

    // `vertical` = the axis runs top-to-bottom on the left (every y-axis). Its
    // centred name is placed nameGap px to the LEFT, where it can collide with
    // the tick labels — so it needs a larger gap than a horizontal (x) axis,
    // whose name simply sits nameGap px below the labels.
    const apply = (axis: Record<string, unknown> | undefined, vertical: boolean, axisIdx: number): void => {
      if (!axis || !axis['name']) {
        return;
      }
      if (axis['nameLocation'] == null) {
        axis['nameLocation'] = 'middle';
      }
      if (axis['nameGap'] == null) {
        // nameGap is measured from the axis line, and the tick labels extend
        // outward from that same line — so the gap MUST exceed the label width
        // or the centred name overlaps the labels (the bug seen on value axes
        // with wide numbers like "100000"). containLabel:true then absorbs this
        // gap into the auto-sized margin, so a generous gap does NOT create dead
        // space — only a large fixed grid.left does. Category (text) labels are
        // wider, so they need the most clearance.
        if (vertical && axis['type'] === 'value') {
          // Value axis: clear the widest formatted tick label plus padding, with
          // a 52px floor so short labels still get a reasonable gap.
          const widest = valueLabelPxByAxis[axisIdx] ?? 0;
          axis['nameGap'] = Math.max(52, Math.round(widest + 14));
        } else {
          axis['nameGap'] = vertical ? (axis['type'] === 'category' ? 72 : 52) : 30;
        }
      }
      if (axis['nameTextStyle'] == null) {
        axis['nameTextStyle'] = { fontWeight: 600, color: '#475467' };
      }
      // A secondary value axis on the right (combined / bar-line charts) places
      // its centred name to the RIGHT of the axis line — the default grid.right
      // is too tight, so it gets clipped / overlaps the right tick labels. Flag
      // it (and how wide its labels are) so we widen the grid below.
      if (axis['position'] === 'right') {
        hasRightNamedAxis = true;
        rightAxisLabelPx = Math.max(rightAxisLabelPx, valueLabelPxByAxis[axisIdx] ?? 0);
      }
    };
    const normalize = (a: unknown, vertical: boolean): void => {
      if (Array.isArray(a)) {
        a.forEach((item, idx) => apply(item as Record<string, unknown>, vertical, idx));
      } else if (a) {
        apply(a as Record<string, unknown>, vertical, 0);
      }
    };
    normalize((option as Record<string, unknown>)['xAxis'], false);
    normalize((option as Record<string, unknown>)['yAxis'], true);

    if (hasRightNamedAxis) {
      const grid = (option as Record<string, unknown>)['grid'] as Record<string, unknown> | undefined;
      if (grid) {
        // Reserve room for the right axis name + its widest tick label so the
        // rotated name ("Trend"/"Units") doesn't sit on top of the numbers.
        const reserve = Math.round(64 + rightAxisLabelPx);
        grid['right'] = Math.max(reserve, Number(grid['right'] || 24) + 40);
      }
    }
    return option;
  }

  /** Per-yAxis-index estimate of the widest value-axis tick label in pixels,
   *  by running each value axis's `axisLabel.formatter` over its series data
   *  extremes. Used to size nameGap (left) and grid.right (right) so the centred
   *  axis name never lands on top of wide tick labels (e.g. "700,000%"). */
  private estimateValueLabelPxByAxis(option: echarts.EChartsOption): Record<number, number> {
    const opt = option as Record<string, unknown>;
    const yAxes = Array.isArray(opt['yAxis']) ? (opt['yAxis'] as Record<string, unknown>[]) : [opt['yAxis'] as Record<string, unknown>];
    const series = (Array.isArray(opt['series']) ? opt['series'] : opt['series'] ? [opt['series']] : []) as Array<Record<string, unknown>>;

    const numericFrom = (datum: unknown): number => {
      if (typeof datum === 'number') return datum;
      if (Array.isArray(datum)) return Number(datum[1]);
      if (datum && typeof datum === 'object' && 'value' in (datum as Record<string, unknown>)) {
        return Number((datum as Record<string, unknown>)['value']);
      }
      return Number(datum);
    };

    const result: Record<number, number> = {};
    yAxes.forEach((axis, axisIdx) => {
      if (!axis || axis['type'] !== 'value') return;
      const formatter = (axis['axisLabel'] as Record<string, unknown> | undefined)?.['formatter'];
      const format = typeof formatter === 'function' ? (formatter as (v: number) => string) : (v: number) => String(v);
      const values: number[] = [];
      series
        .filter((s) => (Number(s['yAxisIndex']) || 0) === axisIdx)
        .forEach((s) => {
          (s['data'] as unknown[] | undefined)?.forEach((d) => {
            const n = numericFrom(d);
            if (Number.isFinite(n)) values.push(n);
          });
        });
      if (!values.length) return;
      let widestChars = 0;
      [Math.min(...values), Math.max(...values)].forEach((v) => {
        try {
          widestChars = Math.max(widestChars, String(format(v)).length);
        } catch {
          /* formatter may assume richer params; ignore and keep the floor */
        }
      });
      // ~7px per character at the default ~12px axis-label font.
      result[axisIdx] = widestChars * 7;
    });
    return result;
  }

  /** Bar + line combo with a secondary y-axis. Primary yAxis renders as bars,
   *  yAxis2 (if set) renders as a line — same pattern the DVM combined-chart
   *  components use. */
  private buildBarLineOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const y2Key = mapping.yAxis2 || '';
    // Year columns on a value axis must not be summed (2023+2023=4046); collapse
    // additive aggregations to a non-additive one so the value stays a real year.
    const yAggregation = this.dataUtils.resolveValueAggregation(dataset, yKey, aggregation);
    const y2Aggregation = this.dataUtils.resolveValueAggregation(dataset, y2Key, aggregation);
    const palette = this.dataUtils.getPaletteColors(style.palette);
    const labelConfig = this.buildSeriesLabel(style);
    const useMultiSeries = !!seriesField && dataset.columns.some((c) => c.name === seriesField) && seriesField !== xKey;
    const hasSecondary = !!y2Key && dataset.columns.some((c) => c.name === y2Key);

    let categories: string[];
    const series: echarts.SeriesOption[] = [];
    // Capture per-category bar totals so we can synthesise a "Total" line on
    // top of the stack when yAxis2 isn't explicitly set. Without this, Mixed
    // Stackbar with auto-default seriesField rendered as just stacked bars.
    let barTotals: number[] | null = null;

    if (useMultiSeries) {
      const grouped = this.dataUtils.aggregateByCategoryAndSeries(dataset, xKey, yKey, seriesField, yAggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
      categories = grouped.categories;
      barTotals = grouped.data.map((row) => row.reduce((acc, v) => acc + (Number(v) || 0), 0));
      grouped.seriesNames.forEach((name, idx) => {
        series.push({
          name,
          type: 'bar',
          data: grouped.data.map((row) => row[idx]),
          stack: 'total',
          itemStyle: { color: palette[idx % palette.length] },
          label: labelConfig as unknown as Record<string, unknown>,
        } as echarts.SeriesOption);
      });
    } else {
      const aggregated = this.dataUtils.aggregateByCategory(dataset, xKey, yKey, yAggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
      categories = aggregated.map((item) => item.name);
      series.push({
        name: yKey || 'Value',
        type: 'bar',
        data: aggregated.map((item) => item.value),
        itemStyle: { color: palette[0] },
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption);
    }

    if (hasSecondary) {
      const y2Aggregated = this.dataUtils.aggregateByCategory(dataset, xKey, y2Key, y2Aggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
      const y2Values = categories.map((cat) => {
        const match = y2Aggregated.find((item) => item.name === cat);
        return match ? match.value : 0;
      });
      series.push({
        name: y2Key,
        type: 'line',
        yAxisIndex: 1,
        data: y2Values,
        smooth: this.lineSmooth(style),
        itemStyle: { color: palette[(useMultiSeries ? series.length : 1) % palette.length] },
        lineStyle: { width: 2.5 },
        symbol: 'circle',
        symbolSize: 6,
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption);
    } else if (useMultiSeries && barTotals) {
      // No explicit yAxis2 — overlay a "Total" line so the bar-line combo
      // shape is visible (sum across all stack segments per category).
      series.push({
        name: 'Total',
        type: 'line',
        yAxisIndex: 1,
        data: barTotals,
        smooth: this.lineSmooth(style),
        itemStyle: { color: palette[series.length % palette.length] },
        lineStyle: { width: 2.5 },
        symbol: 'circle',
        symbolSize: 6,
      } as echarts.SeriesOption);
    } else {
      // Single-series fallback — synthesise a running-average trend line so
      // the preview still conveys "bar + line combo" intent.
      const values = (series[0] as { data: number[] }).data;
      const trend = values.map((_, idx) => {
        const window = values.slice(0, idx + 1);
        return window.reduce((acc, v) => acc + (Number(v) || 0), 0) / window.length;
      });
      series.push({
        name: 'Trend',
        type: 'line',
        yAxisIndex: 1,
        data: trend,
        smooth: this.lineSmooth(style),
        itemStyle: { color: palette[1 % palette.length] },
        lineStyle: { width: 2.5, type: 'dashed' },
        symbol: 'circle',
        symbolSize: 6,
      } as echarts.SeriesOption);
    }

    // Secondary X-axis (X2): a second category axis below the primary X,
    // showing the X2 column's value for each primary X category (uses the
    // first row matched per category). Same pattern as buildAxisChartOption.
    const x2Key = mapping.xAxis2 || '';
    const hasX2 = !!x2Key && dataset.columns.some((c) => c.name === x2Key);
    let xAxisConfig: unknown = { type: 'category', data: categories, name: style.xAxisLabel || xKey || '' };
    if (hasX2) {
      const x2ByCategory = new Map<string, string>();
      dataset.rows.forEach((row) => {
        const cat = String(row[xKey] || '(blank)');
        if (!x2ByCategory.has(cat)) {
          x2ByCategory.set(cat, String(row[x2Key] ?? ''));
        }
      });
      const x2Categories = categories.map((cat) => x2ByCategory.get(cat) ?? '');
      // Lay the two bottom X axes out as distinct labelled rows so their names
      // don't collide: primary labels → primary name → X2 labels → X2 name.
      xAxisConfig = [
        { type: 'category', data: categories, name: style.xAxisLabel || xKey || '', nameLocation: 'middle', nameGap: 16, nameTextStyle: { fontWeight: 600, color: '#475467' } },
        {
          type: 'category',
          data: x2Categories,
          name: style.xAxisLabel2 || x2Key,
          nameLocation: 'middle',
          nameGap: 20,
          nameTextStyle: { fontWeight: 600, color: '#475467' },
          position: 'bottom',
          offset: 38,
          splitLine: { show: false },
          axisTick: { show: true },
          axisLine: { show: true },
        },
      ];
    }

    const formatter = this.buildAxisLabelFormatter(style);
    const formatter2 = this.buildAxisLabelFormatter2(style);
    const gridConfig = this.buildGridConfig(style, false) as Record<string, unknown>;
    if (hasX2) {
      gridConfig['bottom'] = Math.max(80, Number(gridConfig['bottom'] || 40) + 44);
    }

    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: gridConfig,
      tooltip: this.buildCustomTooltip(style, 'bar-line', this.buildDualAxisTooltipFormatter(style, dataset, xKey), 'axis', 'cross'),
      xAxis: xAxisConfig as echarts.EChartsOption['xAxis'],
      yAxis: [
        { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter } },
        { type: 'value', name: style.yAxisLabel2 || y2Key || (useMultiSeries ? 'Total' : 'Trend'), position: 'right', axisLabel: { formatter: formatter2 } },
      ],
      series,
    } as echarts.EChartsOption;
  }

  /** Scatter with size dimension. If `size` mapping is set, point radius
   *  reflects the value; otherwise falls back to fixed-radius bubbles so the
   *  preview still looks like a bubble chart, not a flat scatter. */
  private buildBubbleOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const sizeKey = mapping.size || '';
    const colorByKey = mapping.colorBy || seriesField || '';
    const palette = this.dataUtils.getPaletteColors(style.palette);

    const xValues = dataset.rows.map((row) => this.dataUtils.toNumber(row[xKey]));
    const yValues = dataset.rows.map((row) => this.dataUtils.toNumber(row[yKey]));
    const sizeValues = sizeKey
      ? dataset.rows.map((row) => this.dataUtils.toNumber(row[sizeKey]))
      : [];
    const sizeMax = sizeValues.length ? Math.max(...sizeValues, 1) : 1;
    const minRadius = 8;
    const maxRadius = 36;

    const useGroups = !!colorByKey && dataset.columns.some((c) => c.name === colorByKey);
    const groups = new Map<string, Array<[number, number, number]>>();
    if (useGroups) {
      dataset.rows.forEach((row, idx) => {
        const group = String(row[colorByKey] || '(blank)');
        const point: [number, number, number] = [xValues[idx], yValues[idx], sizeValues[idx] ?? 0];
        const list = groups.get(group) || [];
        list.push(point);
        groups.set(group, list);
      });
    }

    const symbolSize = (value: unknown): number => {
      if (!sizeKey) return minRadius + 6;
      const raw = Array.isArray(value) ? Number(value[2]) : Number(value);
      if (!Number.isFinite(raw) || raw <= 0) return minRadius;
      return minRadius + (raw / sizeMax) * (maxRadius - minRadius);
    };

    const series: echarts.SeriesOption[] = useGroups
      ? Array.from(groups.entries()).map(([name, data], idx) => ({
          name,
          type: 'scatter',
          data,
          symbolSize,
          itemStyle: { color: palette[idx % palette.length], opacity: 0.7 },
        } as echarts.SeriesOption))
      : [{
          name: yKey || 'Series',
          type: 'scatter',
          data: xValues.map((x, i) => [x, yValues[i], sizeValues[i] ?? 0]),
          symbolSize,
          itemStyle: { color: palette[0], opacity: 0.75 },
        } as echarts.SeriesOption];

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: this.buildGridConfig(style, false),
      tooltip: this.buildCustomTooltip(style, 'bubble', undefined, 'item'),
      xAxis: { type: 'value', name: style.xAxisLabel || xKey || '', axisLabel: { formatter } },
      yAxis: { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter } },
      series,
    } as echarts.EChartsOption;
  }

  /** Quadrant Bubble: scatter plot with 4 coloured quadrant backgrounds,
   *  dashed crosshair dividers at data midpoints, and optional bubble sizing. */
  private buildQuadrantBubbleOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const sizeKey = mapping.size || '';
    const colorByKey = mapping.colorBy || seriesField || '';
    const palette = this.dataUtils.getPaletteColors(style.palette);

    // Compute data extent for midpoints and axis padding
    const allXRaw = dataset.rows.map(r => this.dataUtils.toNumber(r[xKey]));
    const allYRaw = dataset.rows.map(r => this.dataUtils.toNumber(r[yKey]));
    const xMin = Math.min(...allXRaw);
    const xMax = Math.max(...allXRaw);
    const yMin = Math.min(...allYRaw);
    const yMax = Math.max(...allYRaw);
    const xPad = (xMax - xMin) * 0.08 || 1;
    const yPad = (yMax - yMin) * 0.08 || 1;
    const xMid = (xMin + xMax) / 2;
    const yMid = (yMin + yMax) / 2;
    const xL = xMin - xPad;
    const xR = xMax + xPad;
    const yB = yMin - yPad;
    const yT = yMax + yPad;

    // Size scaling
    const sizeValues = sizeKey ? dataset.rows.map(r => this.dataUtils.toNumber(r[sizeKey])) : [];
    const sizeMax = sizeValues.length ? Math.max(...sizeValues, 1) : 1;
    const minR = 10;
    const maxR = 40;
    const symbolSize = (value: unknown): number => {
      if (!sizeKey || !sizeValues.length) return 18;
      const raw = Array.isArray(value) ? Number(value[2]) : Number(value);
      if (!Number.isFinite(raw) || raw <= 0) return minR;
      return minR + (raw / sizeMax) * (maxR - minR);
    };

    // Build scatter series grouped by colorBy / seriesField
    let series: echarts.SeriesOption[];
    if (colorByKey && dataset.columns.some(c => c.name === colorByKey)) {
      const grouped = new Map<string, number[][]>();
      dataset.rows.forEach(row => {
        const key = row[colorByKey] || '(blank)';
        const pts = grouped.get(key) || [];
        const pt: number[] = [this.dataUtils.toNumber(row[xKey]), this.dataUtils.toNumber(row[yKey])];
        if (sizeKey) pt.push(this.dataUtils.toNumber(row[sizeKey]));
        pts.push(pt);
        grouped.set(key, pts);
      });
      series = Array.from(grouped.entries()).map(([name, data], idx) => ({
        name,
        type: 'scatter',
        data,
        symbolSize,
        itemStyle: { color: palette[idx % palette.length], opacity: 0.85 },
      } as echarts.SeriesOption));
    } else {
      const points = dataset.rows.map(row => {
        const pt: number[] = [this.dataUtils.toNumber(row[xKey]), this.dataUtils.toNumber(row[yKey])];
        if (sizeKey) pt.push(this.dataUtils.toNumber(row[sizeKey]));
        return pt;
      });
      series = [{ name: yKey || 'Value', type: 'scatter', data: points, symbolSize, itemStyle: { color: palette[0], opacity: 0.85 } } as echarts.SeriesOption];
    }

    // Quadrant background colours — light tints matching reference (BL, BR, TL, TR)
    const Q = [
      'rgba(239,68,68,0.09)',    // bottom-left: light red
      'rgba(245,158,11,0.09)',   // bottom-right: light amber
      'rgba(16,185,129,0.09)',   // top-left: light green
      'rgba(59,130,246,0.09)',   // top-right: light blue
    ];

    // Attach quadrant markArea + crosshair markLine to the first series
    const firstSeries = series[0] as Record<string, unknown>;
    firstSeries['markArea'] = {
      silent: true,
      data: [
        [{ coord: [xL, yB], itemStyle: { color: Q[0] } }, { coord: [xMid, yMid] }],
        [{ coord: [xMid, yB], itemStyle: { color: Q[1] } }, { coord: [xR, yMid] }],
        [{ coord: [xL, yMid], itemStyle: { color: Q[2] } }, { coord: [xMid, yT] }],
        [{ coord: [xMid, yMid], itemStyle: { color: Q[3] } }, { coord: [xR, yT] }],
      ],
    };
    firstSeries['markLine'] = {
      silent: true,
      symbol: ['none', 'none'],
      lineStyle: { color: '#6b7280', type: 'dashed', width: 1.5, opacity: 0.55 },
      label: { show: false },
      data: [{ xAxis: xMid }, { yAxis: yMid }],
    };

    const labelInsetX = (xR - xL) * 0.035;
    const labelInsetY = (yT - yB) * 0.055;
    const quadrantLabels = [
      { name: style.quadrantBottomLeftLabel || '', coord: [xL + labelInsetX, yB + labelInsetY], label: { align: 'left', verticalAlign: 'bottom' } },
      { name: style.quadrantBottomRightLabel || '', coord: [xR - labelInsetX, yB + labelInsetY], label: { align: 'right', verticalAlign: 'bottom' } },
      { name: style.quadrantTopLeftLabel || '', coord: [xL + labelInsetX, yT - labelInsetY], label: { align: 'left', verticalAlign: 'top' } },
      { name: style.quadrantTopRightLabel || '', coord: [xR - labelInsetX, yT - labelInsetY], label: { align: 'right', verticalAlign: 'top' } },
    ].filter((item) => item.name.trim());
    if (quadrantLabels.length) {
      firstSeries['markPoint'] = {
        silent: true,
        symbol: 'circle',
        symbolSize: 0,
        label: {
          show: true,
          formatter: '{b}',
          color: '#344054',
          fontWeight: 700,
          fontSize: 13,
          backgroundColor: 'rgba(255,255,255,0.72)',
          borderColor: 'rgba(148,163,184,0.45)',
          borderWidth: 1,
          borderRadius: 4,
          padding: [4, 8],
        },
        data: quadrantLabels,
      };
    }

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: this.buildGridConfig(style, false),
      tooltip: this.buildCustomTooltip(style, 'quadrant-bubble', undefined, 'item'),
      xAxis: { type: 'value', name: style.xAxisLabel || xKey || '', axisLabel: { formatter }, min: xL, max: xR },
      yAxis: { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter }, min: yB, max: yT },
      series,
    } as echarts.EChartsOption;
  }

  /** Simple Gantt-style timeline: each row becomes one horizontal bar from
   *  xAxis (start) to yAxis (end / numeric position), grouped by category
   *  (seriesField if present, else xAxis). */
  private buildGanttOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const startKey = mapping.xAxis || '';
    const endKey = mapping.yAxis || '';
    const categoryKey = seriesField && dataset.columns.some((c) => c.name === seriesField) ? seriesField : startKey;
    const palette = this.dataUtils.getPaletteColors(style.palette);

    const sameColumn = startKey === endKey;
    const tasks: Array<{ category: string; start: number; end: number; label: string }> = [];
    dataset.rows.forEach((row) => {
      const rawA = this.dataUtils.toNumber(row[startKey]);
      const rawB = this.dataUtils.toNumber(row[endKey]);
      // When the same column is mapped to both axes (e.g., Year?Year), each row
      // gets a 1-unit duration so the Gantt shows a timeline rather than zero bars.
      const start = sameColumn ? rawA : Math.min(rawA, rawB);
      const end = sameColumn ? rawA + 1 : Math.max(rawA, rawB);
      if (end <= start) return;
      tasks.push({
        category: String(row[categoryKey] || '(blank)'),
        start,
        end,
        label: String(row[categoryKey] || ''),
      });
    });

    // If all rows were filtered (e.g., all values are 0), fall back to horizontal bar.
    if (!tasks.length) {
      const catCol = categoryKey !== startKey ? categoryKey : ((dataset.columns.filter((c) => c.type !== 'empty'))[0]?.name || startKey);
      return this.buildAxisChartOption('bar', { xAxis: catCol, yAxis: startKey }, 'sum', dataset, style, '', titleText, { horizontal: true });
    }

    const categories = Array.from(new Set(tasks.map((t) => t.category)));
    // Each "bar" is positioned by start (transparent placeholder) + duration
    // (visible coloured segment) stacked together — standard ECharts Gantt trick.
    const placeholderData: Array<{ value: number; itemStyle: Record<string, unknown> }> = [];
    const durationData: Array<{ value: number; itemStyle: Record<string, unknown> }> = [];
    const yIndexByCategory = new Map<string, number>(categories.map((c, idx) => [c, idx]));

    categories.forEach(() => {
      placeholderData.push({ value: 0, itemStyle: { color: 'transparent' } });
      durationData.push({ value: 0, itemStyle: { color: palette[0] } });
    });

    // For multi-row Gantt, fold all tasks into one row per category by taking
    // earliest start + latest end (a "bar = lifetime of category" preview).
    const aggregatedByCategory = new Map<string, { start: number; end: number }>();
    for (const task of tasks) {
      const existing = aggregatedByCategory.get(task.category);
      if (!existing) {
        aggregatedByCategory.set(task.category, { start: task.start, end: task.end });
      } else {
        existing.start = Math.min(existing.start, task.start);
        existing.end = Math.max(existing.end, task.end);
      }
    }
    aggregatedByCategory.forEach((range, category) => {
      const idx = yIndexByCategory.get(category);
      if (idx === undefined) return;
      placeholderData[idx] = { value: range.start, itemStyle: { color: 'transparent', borderColor: 'transparent' } };
      durationData[idx] = { value: range.end - range.start, itemStyle: { color: palette[idx % palette.length] } };
    });

    // Start the x-axis from the earliest task start so bars are visible even
    // when start values are large numbers (e.g. year 2020). Without this, a
    // bar spanning 2020?2025 is only 5/2500 wide and invisible.
    const allStarts = Array.from(aggregatedByCategory.values()).map(r => r.start);
    const xAxisMin = allStarts.length && Math.min(...allStarts) > 0 ? Math.min(...allStarts) : undefined;

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: { show: false },
      grid: this.buildGridConfig(style, true),
      tooltip: this.buildCustomTooltip(style, 'gantt', (params: unknown): string => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const list = (Array.isArray(params) ? params : [params]) as any[];
          const visible = list.find((p: any) => p?.seriesName !== 'Start');
          const placeholder = list.find((p: any) => p?.seriesName === 'Start');
          if (!visible) return '';
          const startVal = Number(placeholder?.value ?? 0);
          const endVal = startVal + Number(visible.value ?? 0);
          return `${visible.name}<br/>Start: ${this.dataUtils.formatNumberTooltip(startVal, style)}<br/>End: ${this.dataUtils.formatNumberTooltip(endVal, style)}`;
        }, 'axis', 'shadow'),
      xAxis: { type: 'value', min: xAxisMin, name: style.xAxisLabel || startKey || '', axisLabel: { formatter } },
      yAxis: { type: 'category', data: categories, name: style.yAxisLabel || categoryKey || '' },
      series: [
        {
          name: 'Start',
          type: 'bar',
          stack: 'total',
          silent: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent' },
          emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
          data: placeholderData,
        },
        {
          name: 'Duration',
          type: 'bar',
          stack: 'total',
          data: durationData,
          label: this.buildSeriesLabel(style) as unknown as Record<string, unknown>,
        },
      ],
    } as echarts.EChartsOption;
  }

  /** ECharts waterfall pattern: stack a transparent placeholder bar (running
   *  pre-balance) with a coloured delta bar so each step shows the cumulative
   *  contribution. Positive and negative deltas use separate visible series so
   *  they can be coloured independently. */
  private buildWaterfallOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const palette = this.dataUtils.getPaletteColors(style.palette);
    const positiveColor = palette[0] || '#10b981';
    const negativeColor = palette[1] || palette[0] || '#ef4444';

    const aggregated = this.dataUtils.aggregateByCategory(dataset, xKey, yKey, aggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
    const categories = aggregated.map((entry) => entry.name);
    const values = aggregated.map((entry) => Number(entry.value) || 0);

    const placeholder: Array<number | string> = [];
    const positive: Array<number | string> = [];
    const negative: Array<number | string> = [];
    let running = 0;
    for (const value of values) {
      if (value >= 0) {
        placeholder.push(running);
        positive.push(value);
        negative.push('-');
      } else {
        placeholder.push(running + value);
        positive.push('-');
        negative.push(-value);
      }
      running += value;
    }

    const labelConfig = this.buildSeriesLabel(style);
    const valueAxisFormatter = this.buildAxisLabelFormatter(style);

    return {
      title: this.buildTitleConfig(style, titleText),
      legend: { ...this.buildLegendConfig(style), data: ['Increase', 'Decrease'] },
      grid: this.buildGridConfig(style, false),
      tooltip: this.buildCustomTooltip(style, 'waterfall', (params: unknown): string => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const list = (Array.isArray(params) ? params : [params]) as any[];
          const real = list.find((p: any) => p?.seriesName !== 'Placeholder' && p?.value !== '-' && p?.value != null);
          if (!real) return '';
          const raw = Number(real.value);
          return `${real.name}<br/>${real.seriesName}: ${this.dataUtils.formatNumberTooltip(raw, style)}`;
        }, 'axis', 'shadow'),
      xAxis: {
        type: 'category',
        data: categories,
        name: style.xAxisLabel || xKey || '',
        // Bottom of the plot, not y=0 — see the same setting in the published
        // waterfall builder. A negative running total would otherwise push the
        // axis line and its labels into the middle of the chart.
        axisLine: { onZero: false },
      },
      yAxis: {
        type: 'value',
        name: style.yAxisLabel || yKey || '',
        axisLabel: { formatter: valueAxisFormatter },
      },
      series: [
        {
          name: 'Placeholder',
          type: 'bar',
          stack: 'total',
          silent: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent' },
          emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
          data: placeholder,
        },
        {
          name: 'Increase',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: positiveColor },
          label: labelConfig as unknown as Record<string, unknown>,
          data: positive,
        },
        {
          name: 'Decrease',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: negativeColor },
          label: labelConfig as unknown as Record<string, unknown>,
          data: negative,
        },
      ],
    } as echarts.EChartsOption;
  }

  /** Waterfall Stack: each series contributes a stacked segment per category,
   *  and the running total across categories creates a waterfall cumulative
   *  effect. Without a seriesField it falls back to a plain waterfall. */
  private buildWaterfallStackOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const palette = this.dataUtils.getPaletteColors(style.palette);
    const labelConfig = this.buildSeriesLabel(style);

    const hasMultiSeries = !!seriesField && dataset.columns.some((c) => c.name === seriesField) && seriesField !== xKey;
    if (!hasMultiSeries) {
      return this.buildWaterfallOption(mapping, aggregation, dataset, style, titleText);
    }

    const grouped = this.dataUtils.aggregateByCategoryAndSeries(dataset, xKey, yKey, seriesField, aggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
    const { categories, seriesNames, data } = grouped;

    // For each category compute the cumulative starting offset across all series.
    // Each bar within a category stacks — plus we add one transparent placeholder
    // per category whose height is the running cumulative total up to that point.
    const categoryTotals = data.map(row => row.reduce((s, v) => s + (Number(v) || 0), 0));
    const placeholderData: number[] = [];
    let runningTotal = 0;
    for (const total of categoryTotals) {
      placeholderData.push(runningTotal);
      runningTotal += total;
    }

    const series: echarts.SeriesOption[] = [
      {
        name: '__placeholder__',
        type: 'bar',
        stack: 'wf',
        silent: true,
        itemStyle: { color: 'transparent', borderColor: 'transparent' },
        emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
        data: placeholderData,
      } as echarts.SeriesOption,
      ...seriesNames.map((name, idx) => ({
        name,
        type: 'bar' as const,
        stack: 'wf',
        data: data.map(row => row[idx] ?? 0),
        itemStyle: { color: palette[idx % palette.length] },
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption)),
    ];

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: this.buildGridConfig(style, false),
      tooltip: this.buildCustomTooltip(style, 'waterfall-stack', (params: unknown): string => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const list = ((Array.isArray(params) ? params : [params]) as any[]).filter((p: any) => p?.seriesName !== '__placeholder__');
          const name = String(list[0]?.name ?? '');
          const lines = list.map((p: any) => `${p.marker || ''} ${p.seriesName}: ${this.dataUtils.formatNumberTooltip(Number(p.value ?? 0), style)}`);
          return `<strong>${name}</strong><br/>${lines.join('<br/>')}`;
        }, 'axis', 'shadow'),
      xAxis: { type: 'category', data: categories, name: style.xAxisLabel || xKey || '' },
      yAxis: { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter } },
      series,
    } as echarts.EChartsOption;
  }

  /** Whether a title will actually render — explicit override OR the fallback
   *  title text (chart name) passed to buildChartOption. Legend/grid spacing
   *  must account for the fallback title too, else a top legend overlaps it. */
  private hasRenderedTitle(style: ChartStyle): boolean {
    return !!((style.titleOverride || this.currentTitleText || '').trim());
  }

  private buildLegendConfig(style: ChartStyle): Record<string, unknown> {
    const base: Record<string, unknown> = { show: style.showLegend, type: 'scroll' };
    // A centred title sits at top:4 (~22px tall). A top legend must clear it,
    // otherwise the two overlap (the reported bug). When there's no title the
    // legend can sit near the very top.
    const hasTitle = this.hasRenderedTitle(style);
    switch (style.legendPosition) {
      // Horizontal legends are width-constrained so a long legend wraps/scrolls
      // instead of bleeding off the card edges over the title.
      case 'top': return { ...base, top: hasTitle ? 30 : 8, left: 'center' };
      // Vertical legends: start BELOW the title (not top:'middle', which centres
      // over the full height and runs the top items up into the title row — the
      // reported header overlap). Cap width so long names don't run under the plot.
      case 'left': return { ...base, left: 8, top: hasTitle ? 34 : 8, orient: 'vertical', width: 96 };
      case 'right': return { ...base, right: 8, top: hasTitle ? 34 : 8, orient: 'vertical', width: 96 };
      case 'bottom':
      default: return { ...base, bottom: 8, left: 'center' };
    }
  }

  private buildTitleConfig(style: ChartStyle, fallback: string): Record<string, unknown> {
    const text = (style.titleOverride || fallback || '').trim();
    if (!text) {
      return { show: false };
    }
    return { text, left: 'center', top: 4, textStyle: { fontSize: 14, fontWeight: 600 } };
  }

  private buildGridConfig(style: ChartStyle, horizontal: boolean): Record<string, unknown> {
    // containLabel:true reserves room for the tick LABELS only — it does NOT
    // include the rotated axis NAME, which sits nameGap px beyond the axis line
    // and would otherwise be clipped at the card's left edge. So grid.left must
    // hold the part of the name that pokes outside the labels. Keep it just
    // large enough for the name (no more) to avoid the old dead-space gap.
    // Horizontal bar charts use a bigger category-axis nameGap (90 vs 58), so
    // their name needs a little more reserved room.
    const hasTitle = this.hasRenderedTitle(style);
    const legendOn = style.showLegend;
    const pos = style.legendPosition;

    // Base insets (no legend on that side).
    let top = hasTitle ? 40 : 32;
    let bottom = 40 + 28; // room for the X axis name row
    let left = horizontal ? 48 : 44;
    let right = 24;

    // Reserve space on the SIDE the legend actually occupies so it never sits on
    // top of the title, axis labels or the plot (the reported overlap bug).
    if (legendOn) {
      if (pos === 'top') {
        // Legend row below the title; push the grid down to clear both.
        top = (hasTitle ? 56 : 36) + 24;
      } else if (pos === 'bottom') {
        bottom = 56 + 28;
      } else if (pos === 'left') {
        // Vertical legend width (96) + its gap + the axis name/labels.
        left = Math.max(left, 132);
      } else if (pos === 'right') {
        right = Math.max(right, 132);
      }
    }

    return { left, right, top, bottom, containLabel: true };
  }

  private buildAxisLabelFormatter(style: ChartStyle): (value: number) => string {
    // Axis TICK labels are abbreviated (K/M/B) so a large measure never widens
    // the grid past the plot (ECharts containLabel reserves the widest label's
    // width). Tooltips keep full precision separately.
    return (value: number) => this.dataUtils.formatAxisLabel(Number(value), style, false);
  }

  /** Formatter for the SECONDARY (right) value axis — uses numberFormat2/decimals2
   *  (falling back to the primary format) so each axis can round/format on its own.
   *  Also abbreviated: a percent-formatted revenue axis would otherwise render
   *  "300,000,000%" and collapse the plot. */
  private buildAxisLabelFormatter2(style: ChartStyle): (value: number) => string {
    return (value: number) => this.dataUtils.formatAxisLabel(Number(value), style, true);
  }

  /** Line series smoothing from the style's lineShape (default curved). */
  private lineSmooth(style: ChartStyle): boolean {
    return style.lineShape !== 'straight';
  }

  /** Axis-aware tooltip formatter for dual-axis (bar-line / combo) charts: each
   *  row's value is formatted with the PRIMARY or SECONDARY number format
   *  depending on which Y axis that series belongs to. So a right-axis line
   *  (e.g. a percentage / ratio) rounds independently of the left-axis bars.
   *
   *  When `dataset` + `xKey` are supplied, the user's extra "tooltip columns"
   *  are appended just like the standard axis tooltip does — without them, combo
   *  charts silently dropped the configured tooltip columns. */
  private buildDualAxisTooltipFormatter(
    style: ChartStyle,
    dataset?: UploadedDataset,
    xKey?: string,
  ): (params: unknown) => string {
    const rowsByCategory = this.buildTooltipRowLookup(dataset, xKey);
    return (params: unknown): string => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const list = (Array.isArray(params) ? params : [params]) as any[];
      if (!list.length) return '';
      const name = String(list[0]?.axisValueLabel ?? list[0]?.name ?? '');
      const lines = list.map((p: any) => {
        const onSecondary = Number(p?.axisIndex) === 1; // yAxisIndex 1 = right axis
        const raw = Array.isArray(p?.value) ? Number(p.value[1]) : Number(p?.value ?? 0);
        const formatted = this.dataUtils.formatNumberTooltip(raw, style, onSecondary);
        return `${p?.marker || ''} ${p?.seriesName ?? ''}: ${formatted}`;
      });
      this.appendTooltipColumnLines(lines, rowsByCategory, name, xKey, style);
      return `<strong>${name}</strong><br/>${lines.join('<br/>')}`;
    };
  }

  /** Map of category value → its (first) source row, used to look up extra
   *  tooltip-column values by the hovered category. Empty when no dataset/xKey. */
  private buildTooltipRowLookup(
    dataset?: UploadedDataset,
    xKey?: string,
  ): Map<string, Record<string, string>> {
    const lookup = new Map<string, Record<string, string>>();
    if (!dataset || !xKey) return lookup;
    dataset.rows.forEach((row) => {
      const cat = String(row[xKey] || '(blank)');
      if (!lookup.has(cat)) {
        lookup.set(cat, row);
      }
    });
    return lookup;
  }

  /** Append the user-configured extra tooltip columns for the hovered category
   *  to an existing list of tooltip lines (shared by axis + dual-axis tooltips).
   *  Each extra column gets a marker dot + the tooltip's own text color so it
   *  reads as a first-class row alongside the series, not a muted afterthought. */
  private appendTooltipColumnLines(
    lines: string[],
    rowsByCategory: Map<string, Record<string, string>>,
    categoryName: string,
    xKey?: string,
    style?: ChartStyle,
  ): void {
    const tooltipCols = this.ctx.shapeOptions?.tooltipColumns;
    if (!tooltipCols?.length || !rowsByCategory.size) return;
    const row = rowsByCategory.get(categoryName);
    if (!row) return;
    const textColor = style?.tooltipTextColor || '#f8fafc';
    const marker = `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background-color:${textColor};opacity:0.55"></span>`;
    tooltipCols.forEach((col) => {
      if (row[col] !== undefined && col !== xKey) {
        lines.push(`${marker}<span style="color:${textColor}">${col}: <strong>${row[col]}</strong></span>`);
      }
    });
  }

  private buildSeriesLabel(style: ChartStyle): Record<string, unknown> {
    if (!style.showLabels) {
      return { show: false };
    }
    return {
      show: true,
      position: 'top',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: (params: any): string => {
        const value = params?.value;
        const raw = Array.isArray(value) ? Number(value[1]) : Number(value);
        const formatted = this.dataUtils.formatNumber(raw, style);
        const percent: number | undefined = params?.percent;
        const name: string = String(params?.name ?? '');
        switch (style.labelMode) {
          case 'percent': return percent != null ? `${percent}%` : formatted;
          case 'name-value': return `${name}: ${formatted}`.trim();
          case 'name-percent': return percent != null ? `${name}: ${percent}%` : `${name}: ${formatted}`.trim();
          case 'value':
          default: return formatted;
        }
      },
    };
  }

  private buildAxisChartOption(
    seriesType: 'bar' | 'line',
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
    options: { horizontal?: boolean; area?: boolean; stacked?: boolean; step?: boolean } = {},
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    // A year column on the value axis must not be summed (2023+2023=4046); use a
    // non-additive aggregation so the bar height is the actual year.
    const yAggregation = this.dataUtils.resolveValueAggregation(dataset, yKey, aggregation);
    const palette = this.dataUtils.getPaletteColors(style.palette);
    const labelConfig = this.buildSeriesLabel(style);
    const useMultiSeries = !!seriesField && dataset.columns.some((c) => c.name === seriesField) && seriesField !== xKey;
    const stackId = options.stacked ? 'total' : undefined;
    // Step lines connect points with horizontal/vertical segments rather than a
    // sloped line. Only meaningful for line series.
    const stepOption = options.step && seriesType === 'line' ? { step: 'middle' as const, smooth: false } : {};

    let categories: string[];
    let series: echarts.SeriesOption[];

    if (useMultiSeries) {
      const grouped = this.dataUtils.aggregateByCategoryAndSeries(dataset, xKey, yKey, seriesField, yAggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
      categories = grouped.categories;
      series = grouped.seriesNames.map((name, idx) => ({
        name,
        type: seriesType,
        data: grouped.data.map((row) => row[idx]),
        smooth: seriesType === 'line' && this.lineSmooth(style),
        itemStyle: { color: palette[idx % palette.length] },
        label: labelConfig as unknown as Record<string, unknown>,
        ...(options.area ? { areaStyle: {} } : {}),
        ...(stackId ? { stack: stackId } : {}),
        ...stepOption,
      } as echarts.SeriesOption));
    } else {
      const aggregated = this.dataUtils.aggregateByCategory(dataset, xKey, yKey, yAggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
      categories = aggregated.map((item) => item.name);
      const values = aggregated.map((item) => item.value);
      series = [{
        name: yKey || 'Value',
        type: seriesType,
        data: values,
        smooth: seriesType === 'line' && this.lineSmooth(style),
        itemStyle: { color: palette[0] },
        label: labelConfig as unknown as Record<string, unknown>,
        ...(options.area ? { areaStyle: {} } : {}),
        ...(stackId ? { stack: stackId } : {}),
        ...stepOption,
      } as echarts.SeriesOption];
    }

    // Secondary y-axis series — applied to BOTH single- and multi-series paths
    // so stacked / multi-series charts can also show an extra metric on yAxis2.
    const secondaryYKey = mapping.yAxis2 || '';
    if (secondaryYKey && dataset.columns.some((c) => c.name === secondaryYKey)) {
      const y2Aggregation = this.dataUtils.resolveValueAggregation(dataset, secondaryYKey, aggregation);
      const y2Aggregated = this.dataUtils.aggregateByCategory(dataset, xKey, secondaryYKey, y2Aggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
      const y2Values = categories.map((cat) => {
        const match = y2Aggregated.find((item) => item.name === cat);
        return match ? match.value : 0;
      });
      const secondaryType: 'line' | 'bar' = seriesType === 'bar' ? 'line' : 'bar';
      series.push({
        name: secondaryYKey,
        type: secondaryType,
        yAxisIndex: 1,
        data: y2Values,
        smooth: secondaryType === 'line' && this.lineSmooth(style),
        itemStyle: { color: palette[(series.length) % palette.length] },
        lineStyle: secondaryType === 'line' ? { width: 2.5 } : undefined,
        symbol: secondaryType === 'line' ? 'circle' : undefined,
        symbolSize: secondaryType === 'line' ? 6 : undefined,
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption);
    }

    const refValue = this.ctx.shapeOptions?.referenceValue;
    if (refValue !== undefined && refValue !== null && !options.horizontal && series.length > 0) {
      const firstSeries = series[0] as echarts.SeriesOption & { markLine?: Record<string, unknown> };
      firstSeries.markLine = {
        symbol: 'none',
        data: [{ yAxis: refValue, name: 'Target' }],
        lineStyle: { color: '#ef4444', type: 'dashed', width: 2 },
        label: { formatter: 'Target: {c}' },
      };
    }

    const valueAxis = {
      type: 'value' as const,
      name: style.yAxisLabel || (options.horizontal ? xKey : yKey) || '',
      axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
    };
    const categoryAxis = {
      type: 'category' as const,
      data: categories,
      name: style.xAxisLabel || (options.horizontal ? yKey : xKey) || '',
    };

    const y2Key = mapping.yAxis2 || '';
    const hasY2 = !options.horizontal && !!y2Key && dataset.columns.some((c) => c.name === y2Key);
    const yAxisConfig: unknown = hasY2
      ? [valueAxis, { type: 'value' as const, name: style.yAxisLabel2 || y2Key, position: 'right', axisLabel: { formatter: this.buildAxisLabelFormatter2(style) } }]
      : valueAxis;

    // Secondary X-axis (X2): a second category axis BELOW the primary X axis,
    // showing the X2 column's value for each primary X category (uses the
    // first row matched per category). Lets users pair two labels per bar —
    // e.g. "Region" on the primary axis + "Region code" stacked below it.
    const x2Key = mapping.xAxis2 || '';
    const hasX2 = !options.horizontal && !!x2Key && dataset.columns.some((c) => c.name === x2Key);
    let xAxisConfig: unknown = categoryAxis;
    if (hasX2) {
      const x2ByCategory = new Map<string, string>();
      dataset.rows.forEach((row) => {
        const cat = String(row[xKey] || '(blank)');
        if (!x2ByCategory.has(cat)) {
          x2ByCategory.set(cat, String(row[x2Key] ?? ''));
        }
      });
      const x2Categories = categories.map((cat) => x2ByCategory.get(cat) ?? '');
      // Two stacked bottom X axes were colliding: the primary axis name (centred
      // nameGap px below its line) landed on the X2 labels, and the X2 name on
      // those again. Lay them out as two distinct labelled rows, top→bottom:
      //   primary labels → "Region" → X2 labels → "Quarter"
      // Each name's gap/offset is set explicitly here so normalizeAxisNames
      // (which only fills a null nameGap) leaves these untouched.
      const primaryNameGap = 16;   // primary name just under its labels
      const x2Offset = 38;         // X2 line below primary line, clearing primary name
      xAxisConfig = [
        {
          ...(categoryAxis as Record<string, unknown>),
          nameGap: primaryNameGap,
        },
        {
          type: 'category' as const,
          data: x2Categories,
          name: style.xAxisLabel2 || x2Key,
          nameLocation: 'middle' as const,
          nameGap: 20, // X2 name sits below the X2 labels, not on them
          nameTextStyle: { fontWeight: 600, color: '#475467' },
          position: 'bottom' as const,
          offset: x2Offset,
          splitLine: { show: false },
          axisTick: { show: true },
          axisLine: { show: true },
        },
      ];
    }

    const gridConfig = this.buildGridConfig(style, !!options.horizontal) as Record<string, unknown>;
    if (hasX2) {
      // Reserve room for the full stack: primary labels + "Region" name + X2
      // labels + "Quarter" name. Without this the lower rows clip at the card
      // edge. Kept modest so short cards (240px) still keep a usable plot band —
      // clampGridForShortCards further scales this down proportionally if needed.
      gridConfig['bottom'] = Math.max(80, Number(gridConfig['bottom'] || 40) + 44);
    }

    const tooltipPreviewKind: ChartPreviewKind = options.area ? 'area' : (options.horizontal ? 'horizontal-bar' : (seriesType === 'line' ? 'axis-line' : 'axis-bar'));
    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      // With a secondary Y axis, format each series' tooltip value by the axis it
      // belongs to (primary vs secondary number format). Otherwise use the
      // standard axis tooltip (which also supports extra tooltip columns).
      tooltip: hasY2 && style.tooltipEnabled
        ? this.buildCustomTooltip(style, tooltipPreviewKind, this.buildDualAxisTooltipFormatter(style, dataset, xKey), 'axis')
        : this.buildAxisTooltip(style, dataset, xKey, tooltipPreviewKind),
      legend: this.buildLegendConfig(style),
      grid: gridConfig,
      xAxis: options.horizontal ? valueAxis : (xAxisConfig as echarts.EChartsOption['xAxis']),
      yAxis: options.horizontal ? categoryAxis : (yAxisConfig as echarts.EChartsOption['yAxis']),
      series,
    };
  }

  private resolveTooltipTrigger(style: ChartStyle, previewKind: ChartPreviewKind): 'axis' | 'item' {
    if (style.tooltipTrigger !== 'auto') {
      return style.tooltipTrigger;
    }
    const axisKinds: ChartPreviewKind[] = ['axis-bar', 'axis-line', 'horizontal-bar', 'area'];
    return axisKinds.includes(previewKind) ? 'axis' : 'item';
  }

  private buildTooltipConfig(
    style: ChartStyle,
    previewKind: ChartPreviewKind,
    formatter?: (params: unknown) => string,
  ): Record<string, unknown> {
    if (!style.tooltipEnabled) {
      return { show: false };
    }
    const trigger = this.resolveTooltipTrigger(style, previewKind);
    const config: Record<string, unknown> = {
      trigger,
      show: true,
      backgroundColor: style.tooltipBackground,
      borderColor: style.tooltipBorderColor,
      borderWidth: 1,
      borderRadius: style.tooltipBorderRadius,
      padding: [8, 12],
      textStyle: { color: style.tooltipTextColor, fontSize: style.tooltipFontSize },
      extraCssText: `box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);`,
    };
    if (trigger === 'axis') {
      config['axisPointer'] = style.tooltipAxisPointer === 'none'
        ? { show: false }
        : { type: style.tooltipAxisPointer };
    }
    if (formatter) {
      config['formatter'] = formatter;
    }
    return config;
  }

  /** Styled tooltip for builders that need a bespoke formatter or a fixed
   *  trigger (waterfall, gantt, bar-line, bubble). Honors `style.tooltipEnabled`
   *  and applies the user's tooltip styling — the hardcoded `{ trigger: 'axis' }`
   *  objects these replaced did neither, so a disabled/styled tooltip silently
   *  didn't take effect (the "tooltip not working" report). `forcedTrigger`
   *  overrides the style/auto trigger for charts whose shape requires it. */
  private buildCustomTooltip(
    style: ChartStyle,
    previewKind: ChartPreviewKind,
    formatter?: (params: unknown) => string,
    forcedTrigger?: 'axis' | 'item',
    axisPointer?: 'shadow' | 'cross' | 'line',
  ): Record<string, unknown> {
    if (!style.tooltipEnabled) {
      return { show: false };
    }
    const config = this.buildTooltipConfig(style, previewKind, formatter);
    if (forcedTrigger) {
      config['trigger'] = forcedTrigger;
    }
    if ((forcedTrigger === 'axis' || config['trigger'] === 'axis') && axisPointer && style.tooltipAxisPointer === 'line') {
      // Keep the shape-appropriate pointer (shadow for bars, cross for combos)
      // unless the user explicitly chose one via tooltipAxisPointer.
      config['axisPointer'] = { type: axisPointer };
    }
    return config;
  }

  private buildAxisTooltip(
    style: ChartStyle,
    dataset: UploadedDataset,
    xKey: string,
    previewKind: ChartPreviewKind,
  ): Record<string, unknown> {
    const tooltipCols = this.ctx.shapeOptions?.tooltipColumns;
    if (!tooltipCols?.length || !xKey) {
      return this.buildTooltipConfig(style, previewKind);
    }
    const rowsByCategory = this.buildTooltipRowLookup(dataset, xKey);
    const formatter = (params: unknown): string => {
      const arr = Array.isArray(params) ? params : [params];
      const first = arr[0] as { name?: string };
      const name = String(first?.name ?? '');
      const lines = arr.map((p) => {
        const item = p as { marker?: string; seriesName?: string; value?: number };
        return `${item.marker || ''} ${item.seriesName || ''}: ${this.dataUtils.formatNumberTooltip(Number(item.value ?? 0), style)}`;
      });
      this.appendTooltipColumnLines(lines, rowsByCategory, name, xKey, style);
      return `<strong>${name}</strong><br/>${lines.join('<br/>')}`;
    };
    return this.buildTooltipConfig(style, previewKind, formatter);
  }

  private buildTreemapOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    titleText: string,
  ): echarts.EChartsOption {
    const categoryKey = mapping.category || '';
    const valueKey = mapping.value || '';
    const aggregated = this.dataUtils.aggregateByCategory(dataset, categoryKey, valueKey, aggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
    const palette = this.dataUtils.getPaletteColors(style.palette);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const labelFormatter = (params: any): string => {
      const value = this.dataUtils.formatNumber(Number(params?.value), style);
      const name = String(params?.name ?? '');
      return style.showLabels ? `${name}\n${value}` : name;
    };

    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: this.buildTooltipConfig(style, 'treemap', (params: unknown) => {
        const p = params as { name?: string; value?: number };
        return `${String(p?.name ?? '')}: ${this.dataUtils.formatNumberTooltip(Number(p?.value), style)}`;
      }),
      series: [{
        type: 'treemap',
        roam: false,
        data: aggregated,
        label: { show: true, formatter: labelFormatter },
      }] as unknown as echarts.SeriesOption[],
    };
  }

  private buildPieOption(
    isDonut: boolean,
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    titleText: string,
  ): echarts.EChartsOption {
    const categoryKey = mapping.category || '';
    const valueKey = mapping.value || '';
    const aggregated = this.dataUtils.aggregateByCategory(dataset, categoryKey, valueKey, aggregation, this.ctx.shapeOptions, this.ctx.customAggregations, this.ctx.builtinAggregations);
    const palette = this.dataUtils.getPaletteColors(style.palette);

    const labelConfig: Record<string, unknown> = style.showLabels
      ? {
          show: true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (params: any): string => {
            const formatted = this.dataUtils.formatNumber(Number(params?.value), style);
            const name = String(params?.name ?? '');
            const percent = params?.percent ?? 0;
            switch (style.labelMode) {
              case 'percent': return `${percent}%`;
              case 'name-value': return `${name}: ${formatted}`;
              case 'name-percent': return `${name}: ${percent}%`;
              case 'value':
              default: return formatted;
            }
          },
        }
      : { show: false };

    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: this.buildTooltipConfig(style, isDonut ? 'donut' : 'pie', (params: unknown) => {
        const p = params as { name?: string; value?: number; percent?: number };
        return `${String(p?.name ?? '')}: ${this.dataUtils.formatNumberTooltip(Number(p?.value), style)} (${p?.percent ?? 0}%)`;
      }),
      legend: this.buildLegendConfig(style),
      series: [{
        type: 'pie',
        radius: isDonut ? ['45%', '70%'] : '70%',
        center: ['50%', '50%'],
        data: aggregated,
        label: labelConfig,
      }] as unknown as echarts.SeriesOption[],
    };
  }

  private buildScatterOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const sizeKey = mapping.size || '';
    const colorByKey = mapping.colorBy || '';
    const palette = this.dataUtils.getPaletteColors(style.palette);
    const groupingKey = colorByKey || seriesField;
    const useMultiSeries = !!groupingKey && dataset.columns.some((c) => c.name === groupingKey);
    const hasSize = !!sizeKey && dataset.columns.some((c) => c.name === sizeKey);

    let sizeMin = Infinity;
    let sizeMax = -Infinity;
    if (hasSize) {
      dataset.rows.forEach((row) => {
        const v = this.dataUtils.toNumber(row[sizeKey]);
        if (v < sizeMin) sizeMin = v;
        if (v > sizeMax) sizeMax = v;
      });
      if (sizeMin === Infinity) { sizeMin = 0; sizeMax = 1; }
      if (sizeMin === sizeMax) sizeMax = sizeMin + 1;
    }
    const scaleSize = (v: number): number => {
      if (!hasSize) return 10;
      const ratio = (v - sizeMin) / (sizeMax - sizeMin);
      return 8 + ratio * 32;
    };

    let series: echarts.SeriesOption[];
    if (useMultiSeries) {
      const grouped = new Map<string, number[][]>();
      dataset.rows.forEach((row) => {
        const key = row[groupingKey] || '(blank)';
        const points = grouped.get(key) || [];
        const pt: number[] = [this.dataUtils.toNumber(row[xKey]), this.dataUtils.toNumber(row[yKey])];
        if (hasSize) pt.push(this.dataUtils.toNumber(row[sizeKey]));
        points.push(pt);
        grouped.set(key, points);
      });
      series = Array.from(grouped.entries()).map(([name, points], idx) => ({
        name,
        type: 'scatter',
        data: points,
        symbolSize: hasSize
          ? ((val: unknown) => scaleSize((val as number[])[2] ?? 0))
          : 10,
        itemStyle: { color: palette[idx % palette.length] },
      } as echarts.SeriesOption));
    } else {
      const points = dataset.rows.map((row) => {
        const pt: number[] = [this.dataUtils.toNumber(row[xKey]), this.dataUtils.toNumber(row[yKey])];
        if (hasSize) pt.push(this.dataUtils.toNumber(row[sizeKey]));
        return pt;
      });
      series = [{
        name: yKey || 'Value',
        type: 'scatter',
        data: points,
        symbolSize: hasSize
          ? ((val: unknown) => scaleSize((val as number[])[2] ?? 0))
          : 10,
        itemStyle: { color: palette[0] },
      } as echarts.SeriesOption];
    }

    const x2Key = mapping.xAxis2 || '';
    const hasX2 = !!x2Key && dataset.columns.some((c) => c.name === x2Key);
    const primaryXAxis = {
      type: 'value' as const,
      name: style.xAxisLabel || xKey,
      axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
    };
    let x2Min = Infinity;
    let x2Max = -Infinity;
    if (hasX2) {
      dataset.rows.forEach((row) => {
        const v = this.dataUtils.toNumber(row[x2Key]);
        if (Number.isFinite(v)) {
          if (v < x2Min) x2Min = v;
          if (v > x2Max) x2Max = v;
        }
      });
      if (x2Min === Infinity) { x2Min = 0; x2Max = 1; }
      if (x2Min === x2Max) x2Max = x2Min + 1;
    }
    const xAxisConfig: unknown = hasX2
      ? [
          // Two stacked bottom X axes: lay out as distinct labelled rows so the
          // names don't collide (primary labels → primary name → X2 labels → X2 name).
          { ...primaryXAxis, nameLocation: 'middle' as const, nameGap: 16, nameTextStyle: { fontWeight: 600, color: '#475467' } },
          {
            type: 'value' as const,
            name: style.xAxisLabel2 || x2Key,
            nameLocation: 'middle' as const,
            nameGap: 20,
            nameTextStyle: { fontWeight: 600, color: '#475467' },
            position: 'bottom' as const,
            offset: 38,
            min: x2Min,
            max: x2Max,
            axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
            splitLine: { show: false },
          },
        ]
      : primaryXAxis;

    // Secondary Y-axis (Y2) for scatter — second numeric value axis on the
    // right with its own scale derived from the Y2 column range.
    const y2Key = mapping.yAxis2 || '';
    const hasY2 = !!y2Key && dataset.columns.some((c) => c.name === y2Key);
    const primaryYAxis = {
      type: 'value' as const,
      name: style.yAxisLabel || yKey,
      axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
    };
    let y2Min = Infinity;
    let y2Max = -Infinity;
    if (hasY2) {
      dataset.rows.forEach((row) => {
        const v = this.dataUtils.toNumber(row[y2Key]);
        if (Number.isFinite(v)) {
          if (v < y2Min) y2Min = v;
          if (v > y2Max) y2Max = v;
        }
      });
      if (y2Min === Infinity) { y2Min = 0; y2Max = 1; }
      if (y2Min === y2Max) y2Max = y2Min + 1;
    }
    const yAxisConfig: unknown = hasY2
      ? [
          primaryYAxis,
          {
            type: 'value' as const,
            name: style.yAxisLabel2 || y2Key,
            position: 'right' as const,
            min: y2Min,
            max: y2Max,
            axisLabel: { formatter: this.buildAxisLabelFormatter2(style) },
            splitLine: { show: false },
          },
        ]
      : primaryYAxis;

    const gridConfig = this.buildGridConfig(style, false) as Record<string, unknown>;
    if (hasX2) {
      // X2 sits as a second labelled row below the primary X-axis name; reserve
      // room for primary labels + primary name + X2 labels + X2 name.
      gridConfig['bottom'] = Math.max(80, Number(gridConfig['bottom'] || 40) + 44);
    }
    if (hasY2) {
      gridConfig['right'] = Math.max(56, Number(gridConfig['right'] || 24) + 32);
    }

    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: this.buildTooltipConfig(style, 'scatter'),
      legend: this.buildLegendConfig(style),
      grid: gridConfig,
      xAxis: xAxisConfig as echarts.EChartsOption['xAxis'],
      yAxis: yAxisConfig as echarts.EChartsOption['yAxis'],
      series,
    };
  }
}


